import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSite } from '../context/SiteContext';
import { Moon, Sun, Percent, Tag as TagIcon, Zap } from 'lucide-react';
import FloatingWhatsApp from './FloatingWhatsApp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOTTOM_NAV = [
  { to: '/', label: 'Cupones', icon: Percent },
  { to: '/ofertas', label: 'Ofertas', icon: TagIcon },
  { to: '/proximos-cupones', label: 'Próximos', icon: Zap },
];

const Layout = () => {
  const { settings, getImageUrl, darkMode, toggleDarkMode } = useSite();
  const location = useLocation();
  const [offersCount, setOffersCount] = useState(0);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const navItems = BOTTOM_NAV.filter((i) => i.to !== '/proximos-cupones' || settings?.upcoming_enabled);

  useEffect(() => {
    const fetchOffersCount = async () => {
      try {
        const res = await axios.get(`${API}/coupons?_t=${Date.now()}`);
        const now = new Date();
        const count = (res.data || [])
          .filter((c) => (c.item_kind || 'coupon') === 'product')
          .filter((c) => !c.search_only)
          .filter((c) => !c.expiration_date || new Date(c.expiration_date) >= now)
          .length;
        setOffersCount(count);
      } catch (e) {
        console.error(e);
      }
    };
    const fetchUpcomingCount = async () => {
      try {
        const res = await axios.get(`${API}/upcoming-coupons?_t=${Date.now()}`);
        const count = (res.data || []).filter((c) => c.manual_status !== 'finalizado').length;
        setUpcomingCount(count);
      } catch (e) {
        console.error(e);
      }
    };
    fetchOffersCount();
    fetchUpcomingCount();
  }, [location.pathname]);

  useEffect(() => {
    // iOS (Chrome opened from WhatsApp): the fixed bottom nav can be mispositioned/hidden on the
    // first paint until a reload. Force a reflow shortly after mount and on pageshow (bfcache) so it appears.
    const nudge = () => {
      window.requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        window.scrollTo(0, y + 1);
        window.scrollTo(0, y);
      });
    };
    const t1 = setTimeout(nudge, 250);
    const t2 = setTimeout(nudge, 800);
    window.addEventListener('pageshow', nudge);
    window.addEventListener('orientationchange', nudge);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('pageshow', nudge);
      window.removeEventListener('orientationchange', nudge);
    };
  }, []);

  const darkBg = settings.dark_bg_color || '#0f172a';
  const darkHeader = settings.dark_header_color || '#1e293b';
  const darkText = settings.dark_text_color || '#f1f5f9';

  let headerButtons = [];
  try {
    headerButtons = JSON.parse(settings.header_nav_buttons || '[]');
  } catch (e) {
    headerButtons = [];
  }
  const fontMap = { fredoka: "'Fredoka', sans-serif", nunito: "'Nunito', sans-serif", serif: 'Georgia, serif', mono: 'monospace' };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${!darkMode ? 'bg-slate-50' : ''}`}
      style={darkMode ? { backgroundColor: darkBg } : {}}
    >
      {/* Header */}
      <header
        className="sticky top-0 z-40 w-full shadow-sm transition-colors duration-300"
        style={{ backgroundColor: darkMode ? darkHeader : (settings.header_bg_color || '#FFD600') }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {settings.logo_url && (
              <img 
                src={getImageUrl(settings.logo_url)} 
                alt={settings.site_name} 
                className="w-10 h-10 rounded-full border-2 border-white shadow-sm object-cover"
              />
            )}
            <span
              className="font-fredoka text-2xl font-bold tracking-tight"
              style={{ color: darkMode ? darkText : (settings.header_text_color || '#0f172a') }}
            >
              OFERTOPOLIS
            </span>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {headerButtons.map((btn) => (
              <a
                key={btn.id}
                href={btn.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`header-nav-button-${btn.id}`}
                className="px-4 py-2 rounded-full shadow-sm flex items-center gap-1.5 flex-shrink-0 hover:opacity-90 transition-opacity whitespace-nowrap text-sm sm:text-base"
                style={{
                  backgroundColor: btn.bg_color || '#ffffff',
                  color: btn.text_color || '#1e293b',
                  fontWeight: btn.bold ? 700 : 500,
                  fontFamily: fontMap[btn.font] || undefined
                }}
              >
                {btn.emoji && <span>{btn.emoji}</span>}
                <span>{btn.text}</span>
              </a>
            ))}
            {settings.dark_mode_enabled !== false && (
              <button
                onClick={toggleDarkMode}
                data-testid="dark-mode-toggle"
                title={darkMode ? 'Modo claro' : 'Modo oscuro'}
                className={`p-2.5 rounded-full shadow-sm transition-all active:scale-95 ${darkMode ? 'bg-slate-700 text-yellow-300' : 'bg-white text-slate-700'}`}
              >
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto w-full px-4 py-6">
        <Outlet />
      </main>

      {/* Legal Disclaimer */}
      <footer
        className={`w-full px-4 pt-6 pb-28 ${darkMode ? 'border-t border-slate-700' : 'border-t border-neutral-200 bg-slate-50'}`}
        style={darkMode ? { backgroundColor: darkBg } : {}}
        data-testid="site-disclaimer"
      >
        <div className="max-w-3xl mx-auto text-center">
          <p
            className={`text-xs leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}
          >
            OFERTOPOLIS es un sitio independiente de recopilación de cupones y ofertas. Participamos en el Programa de Afiliados de Mercado Libre, pero no somos Mercado Libre ni representamos oficialmente a la empresa. Algunos enlaces pueden ser enlaces de afiliado. Todas las marcas, logotipos y nombres comerciales pertenecen a sus respectivos propietarios.
          </p>
        </div>
      </footer>

      {/* Bottom Navigation - Global */}
      <nav
        className={`fixed bottom-0 left-0 right-0 border-t z-50 ${darkMode ? 'border-slate-700' : 'bg-white border-neutral-200'}`}
        style={{
          ...(darkMode ? { backgroundColor: darkHeader } : {}),
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          willChange: 'transform',
        }}
        data-testid="bottom-navigation"
      >
        <div className="max-w-md mx-auto grid" style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
                  isActive
                    ? 'text-blue-600'
                    : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    <item.icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5]' : ''}`} />
                    {item.to === '/ofertas' && offersCount > 0 && (
                      <span
                        data-testid="ofertas-badge-count"
                        className="animate-badge-blink absolute -top-2 -right-2.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none shadow-md"
                      >
                        {offersCount}
                      </span>
                    )}
                    {item.to === '/proximos-cupones' && upcomingCount > 0 && (
                      <span
                        data-testid="proximos-badge-count"
                        className="animate-badge-blink absolute -top-2 -right-2.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold leading-none shadow-md"
                      >
                        {upcomingCount}
                      </span>
                    )}
                  </div>
                  <span className={`text-[11px] sm:text-xs font-bold ${isActive ? '' : 'font-medium'}`}>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Floating WhatsApp */}
      <FloatingWhatsApp />
    </div>
  );
};

export default Layout;
